import styled from 'styled-components';
import { s } from './NPL.styles';
import { ReportFormatNumber } from '../../../../func/Function';
import { NplCalculation } from '../../../../utils/nplAnalysis';
import { useMemo, useState } from 'react';
import type { SaleInfo } from '../../data';

interface ISaleDateSimulationProps {
  bidDateSimulation: ReturnType<NplCalculation['getBidDateSimul']>;
  saleInfo?: SaleInfo[];
}

const SaleDateSimulation = (props: ISaleDateSimulationProps) => {
  const { bidDateSimulation, saleInfo = [] } = props;
  const [showMore, setShowMore] = useState(false);

  // 날짜 비교 유틸: 'YYYY.MM.DD HH:mm:ss' 지원, 비교는 일(day)까지만
  const dayPart = (v?: string) =>
    (v?.trim().split(' ')[0] || '').replace(/\./g, '-');
  const sameDay = (a?: string, b?: string) => dayPart(a) === dayPart(b);

  // 렌더링용 행 데이터: saleInfo를 최신→과거로 reverse()하여 사용
  const rows = useMemo(() => {
    const reversed = saleInfo.slice().reverse();

    return reversed.map((info, i) => {
      const sim = bidDateSimulation.find((s) => sameDay(s.date, info.date));
      const statusRaw = ((info?.result || '').split('(')[0] || '').trim();
      const noLabel = info?.no ? `${info.no}차` : '-';

      // 최저가: 시뮬레이션 price 우선, 없으면 saleInfo.minprice 사용
      const minLabel =
        sim?.price != null
          ? ReportFormatNumber(sim.price)
          : ReportFormatNumber(info.minprice as any);

      // 저감률: 시뮬레이션 rate 우선
      const rateLabel =
        typeof sim?.rate === 'number'
          ? ReportFormatNumber(sim.rate, true, true, true)
          : '-';

      return {
        noLabel,
        dateLabel: (info.date || '').split(' ')[0]?.replaceAll('-', '.') || '-',
        minLabel,
        rateLabel,
        statusRaw: statusRaw || '-',
        isLatest: i === 0,
      };
    });
  }, [saleInfo, bidDateSimulation]);

  // 4개까지만, 더보기 시 전체
  const visible = useMemo(
    () => rows.slice(0, showMore ? rows.length : 4),
    [rows, showMore],
  );

  return (
    <s.Div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <s.Title>매각기일 시뮬레이션</s.Title>
        <s.Description>
          <img
            src="/assets/images/icon/info_16.svg"
            alt=""
            style={{ width: 16 }}
          />
          <span>
            이미 경매가 진행 중인 경우에는 해당 경매일정을 반영합니다.
          </span>
        </s.Description>
      </div>

      {/* 헤더 */}
      <SlideDiv style={{ color: '#616161', fontSize: '12px' }}>
        <RowLeft style={{ paddingLeft: '14px' }}>
          <Row>회차/매각기일</Row>
        </RowLeft>
        <RowRight>
          <Row>최저매각가격</Row>
          <Row style={{ width: '50px' }}>저감률</Row>
          <Row style={{ width: '26px' }}>상태</Row>
        </RowRight>
      </SlideDiv>

      {visible.map((r, idx) => (
        <SlideDiv key={`${r.noLabel}-${r.dateLabel}-${idx}`}>
          <RowLeft>
            <Row style={{ justifyContent: 'start' }}>
              <Dot
                src={
                  r.isLatest
                    ? '/assets/images/icon/blue_dot_state.svg'
                    : '/assets/images/icon/gray_dot_state.svg'
                }
                alt=""
              />
              <Column $isLatest={r.isLatest}>
                <span>{r.noLabel}</span>
                <span>{r.dateLabel}</span>
              </Column>
            </Row>
          </RowLeft>
          <RowRight $isLatest={r.isLatest}>
            <Row $isLatest={r.isLatest}>{r.minLabel}</Row>
            <Row style={{ width: '50px' }} $isLatest={r.isLatest}>
              {r.rateLabel}
            </Row>
            <Row style={{ width: '26px' }}>
              <StatusText $status={r.statusRaw}>{r.statusRaw}</StatusText>
            </Row>
          </RowRight>
        </SlideDiv>
      ))}

      {rows.length > 4 && (
        <MoreWrap>
          <MoreBtn
            aria-expanded={showMore}
            onClick={() => setShowMore((v) => !v)}
          >
            {showMore ? '매각기일 접기' : '매각기일 더보기'}
            <Arrow
              src={
                showMore
                  ? '/assets/images/arrow/black_arrow_up_20.svg'
                  : '/assets/images/arrow/black_arrow_down_20.svg'
              }
              alt=""
            />
          </MoreBtn>
        </MoreWrap>
      )}
    </s.Div>
  );
};

export default SaleDateSimulation;

const SlideDiv = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #f3f5f7;
`;

const EmptyDiv = styled.div`
  width: 6px;
  height: 20px;
`;

const Dot = styled.img`
  width: 6px;
  height: 20px;
`;

const Row = styled.div<{ $isLatest?: boolean }>`
  display: flex;
  flex-direction: row;
  gap: 8px;
  font-size: 14px;
  color: ${({ $isLatest }) => ($isLatest ? '#222222' : '#6b7684')};
  font-weight: 500;
  padding-bottom: 12px;
  justify-content: flex-end;
`;

const RowLeft = styled.div`
  display: flex;
  gap: 8px;
`;

const RowRight = styled.div<{ $isLatest?: boolean }>`
  display: flex;
  gap: 8px;
`;

const TableHeader = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  font-size: 12px;
  font-weight: 500;
  color: #616161;
`;

const HeaderLeft = styled.div`
  display: flex;
  padding-left: 14px;
  gap: 8px;
`;

const HeaderRight = styled.div`
  display: flex;
  gap: 8px;
`;

const MoreWrap = styled.div`
  display: flex;
  justify-content: center;
`;

const MoreBtn = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 20px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 500;
  background-color: #f7f7f9;
  color: #616161;
  cursor: pointer;
  border: none;
`;

const Arrow = styled.img`
  width: 17px;
  height: 17px;
`;

const StatusText = styled.span<{ $status: string }>`
  ${({ $status }) => {
    switch ($status) {
      case '진행':
        return 'color:#03B26C;';
      case '유찰':
        return 'color:#F04452;';
      case '변경':
        return 'color:#FFA100;';
      case '매각':
        return 'color:#008AFF;';
      default:
        return 'color:#6B7684;';
    }
  }}
`;

const Column = styled.div<{ $isLatest?: boolean }>`
  display: flex;
  flex-direction: column;
  color: ${({ $isLatest }) => ($isLatest ? '#222222' : '#6b7684')};
`;
